import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as cameraService from '../services/cameraService';
import type {
  CreateCameraRequest,
  UpdateCameraRequest,
  ListQueryParams
} from '../types/api';

// Cameras list query
export const useCameras = (params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.cameras.list(params),
    queryFn: () => cameraService.getCameras(params),
    select: (data) => {
      // API returns { cameras: Camera[], pagination: Pagination }
      // Return consistent structure with cameras array
      return {
        cameras: data.cameras || [],
        pagination: data.pagination || null
      };
    },
  });
};

// Single camera query
export const useCamera = (id: string, view?: string) => {
  return useQuery({
    queryKey: queryKeys.cameras.detail(id),
    queryFn: () => cameraService.getCamera(id, view),
    select: (data) => data,
    enabled: !!id,
  });
};

// Camera overview query for dashboard/monitoring
export const useCamerasOverview = () => {
  return useQuery({
    queryKey: ['cameras', 'overview'],
    queryFn: () => cameraService.getCamerasOverview(),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider stale after 30 seconds
  });
};

// Camera health status query
export const useCameraHealthStatus = (id: string) => {
  return useQuery({
    queryKey: ['cameras', id, 'health'],
    queryFn: () => cameraService.getCameraHealthStatus(id),
    enabled: !!id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Create camera mutation
export const useCreateCamera = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCameraRequest) => cameraService.createCamera(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      notifications.show({
        title: 'Success',
        message: 'Camera created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create camera',
        color: 'red',
      });
    },
  });
};

// Update camera mutation
export const useUpdateCamera = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCameraRequest }) =>
      cameraService.updateCamera(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.detail(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Camera updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update camera',
        color: 'red',
      });
    },
  });
};

// Delete camera mutation
export const useDeleteCamera = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cameraService.deleteCamera(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      notifications.show({
        title: 'Success',
        message: 'Camera deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete camera',
        color: 'red',
      });
    },
  });
};

// Test camera connection mutation
export const useTestCameraConnection = () => {
  return useMutation({
    mutationFn: (id: string) => cameraService.testCameraConnection(id),
    onSuccess: (response) => {
      notifications.show({
        title: 'Connection Test',
        message: (response && response.message) || 'Camera connection successful',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Connection Test Failed',
        message: error.message || 'Failed to connect to camera',
        color: 'red',
      });
    },
  });
};

// Toggle camera status mutation
export const useToggleCameraStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cameraService.toggleCameraStatus(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.detail(variables) });
      notifications.show({
        title: 'Success',
        message: 'Camera status updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to toggle camera status',
        color: 'red',
      });
    },
  });
};

// Bulk operations hooks
export const useBulkToggleCameraStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: boolean }) =>
      cameraService.bulkToggleCameraStatus(ids, status),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });

      if (result.success.length > 0) {
        notifications.show({
          title: 'Bulk Status Update',
          message: `Successfully updated ${result.success.length} camera(s)`,
          color: 'green',
        });
      }

      if (result.failed.length > 0) {
        notifications.show({
          title: 'Partial Success',
          message: `Failed to update ${result.failed.length} camera(s)`,
          color: 'orange',
        });
      }
    },
    onError: (_error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update camera statuses',
        color: 'red',
      });
    },
  });
};

export const useBulkTestCameraConnection = () => {
  return useMutation({
    mutationFn: (ids: string[]) => cameraService.bulkTestCameraConnection(ids),
    onSuccess: (result) => {
      if (result.success.length > 0) {
        notifications.show({
          title: 'Bulk Connection Test',
          message: `Successfully tested ${result.success.length} camera(s)`,
          color: 'green',
        });
      }

      if (result.failed.length > 0) {
        notifications.show({
          title: 'Test Results',
          message: `${result.failed.length} camera(s) failed connection test`,
          color: 'orange',
        });
      }
    },
    onError: (_error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to test camera connections',
        color: 'red',
      });
    },
  });
};

export const useBulkDeleteCameras = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => cameraService.bulkDeleteCameras(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });

      if (result.success.length > 0) {
        notifications.show({
          title: 'Bulk Delete',
          message: `Successfully deleted ${result.success.length} camera(s)`,
          color: 'green',
        });
      }

      if (result.failed.length > 0) {
        notifications.show({
          title: 'Partial Success',
          message: `Failed to delete ${result.failed.length} camera(s)`,
          color: 'orange',
        });
      }
    },
    onError: (_error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete cameras',
        color: 'red',
      });
    },
  });
};
